import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, startWith, Subject, tap } from 'rxjs';
import { InputFieldComponent, SelectFieldComponent } from '../../../form';
import { CustomModalOptions, SmartModalService } from '../../../smart-modal';
import { FIELD_TYPES, SCALE_FIELD_TYPE } from '../../constants/field-types';
import { FieldTypeENUM, IFields } from '../../models';
import { FormBuilderService } from '../../services/builder.service';
import {
  ManageFieldComponent,
  ManageFieldModalData,
  ManageFieldResult,
} from '../manage-field/manage-field.component';
import { SwitchTypeFieldComponent } from './switch-type-field.component';
import { TestConnessioneDialogComponent } from './test-connessione-dialog.component';

@Component({
  selector: 'siat-field-builder',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    forwardRef(() => FieldBuilderComponent),
    SwitchTypeFieldComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatSelectModule,
    MatCardTitle,
    MatCard,
    MatCardContent,
    DragDropModule,
    InputFieldComponent,
    SelectFieldComponent,
  ],
  templateUrl: './field-builder.component.html',
  styleUrls: ['./field-builder.component.scss'],
})
@UntilDestroy()
export class FieldBuilderComponent implements OnInit {
  /**
   * The form group for the field.
   */
  @Input()
  fieldForm: FormGroup;

  /**
   * The index of the field.
   */
  @Input()
  fieldIndex: number;

  /**
   * The index of the section.
   */
  @Input()
  sectionIndex: number;

  /**
   * The id of the field.
   */
  @Input()
  fieldId: string;

  /**
   * The id of the section.
   */
  @Input()
  sectionId: string;

  /**
   * A flag to indicate if drag and drop is enabled.
   */
  @Input()
  dragFieldsEnabled = false;

  /**
   * Indicates whether this field is rendered inside an accordion container.
   */
  @Input()
  isAccordionChild = false;

  /**
   * Emits an event when the duplicate button is clicked.
   */
  @Output()
  duplicate = new EventEmitter<string>();

  /**
   * Emits an event when the delete button is clicked.
   */
  @Output()
  delete = new EventEmitter<string>();

  /**
   * The dynamic form builder service.
   */
  readonly dynamicFormService = inject(FormBuilderService);

  /**
   * The smart modal service.
   */
  readonly smartModalService = inject(SmartModalService);

  /**
   * The field type ENUM.
   */
  readonly fieldTypeEnum = FieldTypeENUM;

  /**
   * The current type of the field.
   */
  currentType: FieldTypeENUM | undefined;

  /**
   * Subject to handle unsubscription of endpoint listeners.
   */
  private readonly stopEndpointListener$ = new Subject<void>();

  /**
   * Initializes the availableEndpoints signal.
   */
  readonly availableEndpoints = this.dynamicFormService.availableEndpoints;

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.currentType = this.fieldForm.get('type')?.value;
    if (this.isTypologyType) {
      this.dynamicFormService.loadTipologiche();
    }
    this.handleEndpointLogic();
  }

  /**
   * Getter for typology check
   */
  get isTypologyType(): boolean {
    return this.currentType === FieldTypeENUM.TYPOLOGY;
  }

  /**
   * Getter for endpoint control
   */
  get endpointControl(): FormControl {
    return this.fieldForm.get('endpoint') as FormControl;
  }

  /**
   * Handles the change of the field type.
   */
  onTypeChange(changeValue: MatSelectChange<FieldTypeENUM>): void {
    const newType = changeValue.value;
    if (newType === this.currentType) return;

    const allTypes = [...FIELD_TYPES, ...SCALE_FIELD_TYPE];
    const typeConfig = allTypes.find(t => t.type === newType);

    this.dynamicFormService.syncFieldType(
      this.fieldForm,
      newType,
      typeConfig?.config,
      this.currentType
    );
    this.currentType = newType;

    if (newType === FieldTypeENUM.TYPOLOGY) {
      this.dynamicFormService.loadTipologiche();
    }

    this.handleEndpointLogic();
  }

  /**
   * Handles the endpoint logic.
   */
  private handleEndpointLogic(): void {
    this.stopEndpointListener$.next();

    if (!this.isTypologyType) return;

    const control = this.endpointControl;

    if (control) {
      control.valueChanges
        .pipe(
          untilDestroyed(this),
          startWith(control.value),
          filter(val => !!val),
          tap(val => {
            this.dynamicFormService.updateTypologyFieldConfig(
              this.fieldForm,
              val
            );
          })
        )
        .subscribe();
    }
  }

  /**
   * @returns {FormArray} of options.
   */
  get optionsFormArray(): FormArray {
    return this.fieldForm.get('options') as FormArray;
  }

  /**
   * @returns {FormArray} of nested accordion fields.
   */
  get accordionFieldsArray(): FormArray {
    return this.fieldForm.get('fields') as FormArray;
  }

  /**
   * @returns The title shown in the builder card.
   */
  get fieldTitle(): string {
    if (this.isAccordionChild) return `Elemento ${this.fieldIndex} Accordion`;

    if (this.fieldForm.get('type')?.value === FieldTypeENUM.ACCORDION) {
      return `Accordion ${this.fieldIndex} - Sezione ${this.sectionIndex}`;
    }

    return `Elemento ${this.fieldIndex} - Sezione ${this.sectionIndex}`;
  }

  /**
   * @returns The options formgroup by @param index
   */
  getOptionsFormGroup(index: number): FormGroup {
    return this.optionsFormArray.at(index) as FormGroup;
  }

  /**
   * On click add option.
   */
  onClickAddOption(): void {
    this.dynamicFormService.addOptionToField(this.fieldForm);
  }

  /**
   * On click delete option.
   */
  onClickDeleteOption(index: number): void {
    this.dynamicFormService.removeOptionFromField(this.fieldForm, index);
  }

  /**
   * Adds a nested field inside the accordion.
   */
  addFieldToAccordion(): void {
    this.dynamicFormService.addFieldToAccordion(this.fieldForm, this.fieldId);
  }

  /**
   * Duplicates a nested accordion field.
   */
  duplicateAccordionField(fieldFormGroup: FormGroup): void {
    this.dynamicFormService.duplicateAccordionField(
      this.fieldForm,
      fieldFormGroup,
      this.fieldId
    );
  }

  /**
   * Deletes a nested accordion field.
   */
  deleteAccordionField(fieldFormGroup: FormGroup): void {
    this.dynamicFormService.deleteAccordionField(this.fieldForm, fieldFormGroup);
  }

  /**
   * On click for opening the manage field modal.
   */
  onClickManageField(): void {
    const allFieldsInForm = this.getAllOtherFields();
    const fieldForm = this.fieldForm;
    const dynamicFormService = this.dynamicFormService;
    const smartModalService = this.smartModalService;

    const currentRawValue = fieldForm.getRawValue();
    const hasCondition = fieldForm.contains('hasCondition');
    const conditionFieldId = fieldForm.contains('conditionFieldId');
    const conditionValue = fieldForm.contains('conditionValue');

    if (this.isConditionBroken) {
      dynamicFormService.manageConditionalControls(fieldForm, false);
    }

    if (!hasCondition) {
      currentRawValue.hasCondition = false;
    }

    const modalConfig = {
      config: { width: '500px', disableClose: true },
      data: {
        fieldData: currentRawValue,
        availableFields: allFieldsInForm,
      },
    } as CustomModalOptions<ManageFieldModalData>;

    smartModalService
      .openModalFromComponent<ManageFieldComponent, ManageFieldResult>(
        ManageFieldComponent,
        modalConfig
      )
      .pipe(
        filter(data => !!data),
        untilDestroyed(this),
        tap(data => {
          const extractedConditionFieldId =
            typeof data.conditionFieldId === 'object' &&
            data.conditionFieldId
              ? data.conditionFieldId.id
              : data.conditionFieldId;
          const extractedConditionValue =
            typeof data.conditionValue === 'object' && data.conditionValue
              ? data.conditionValue.id
              : data.conditionValue;

          dynamicFormService.manageConditionalControls(fieldForm, !!data.hasCondition, {
            hasCondition: data.hasCondition ?? undefined,
            conditionFieldId: extractedConditionFieldId as string | undefined,
            conditionValue: extractedConditionValue as
              | string
              | number
              | boolean
              | undefined,
          });

          const fieldsToUpdate = {
            required: data?.required,
            min: data?.min ?? data?.minCharts,
            max: data?.max ?? data?.maxCharts,
            ...(hasCondition && { hasCondition: data.hasCondition }),
            ...(conditionFieldId && {
              conditionFieldId: extractedConditionFieldId,
            }),
            ...(conditionValue && { conditionValue: extractedConditionValue }),
          };

          fieldForm.patchValue(fieldsToUpdate);
        })
      )
      .subscribe();
  }

  /**
   * @returns {IFields[]} An array of fields excluding the current one.
   */
  private getAllOtherFields(): IFields[] {
    const sections = this.dynamicFormService.getSectionFormArray();
    if (!sections) return [];

    const allFields: IFields[] = [];
    const excludedTypes = [
      FieldTypeENUM.UPLOADFILE,
      FieldTypeENUM.SECTION,
      FieldTypeENUM.ACCORDION,
      FieldTypeENUM.DATE,
      FieldTypeENUM.REGIONI_PROVINCE_COMUNI,
    ];

    sections.controls.forEach(sectionCtrl => {
      const fieldsArray = sectionCtrl.get('fields') as FormArray;
      if (!fieldsArray) return;

      fieldsArray.controls.forEach(fieldCtrl => {
        const fieldVal = fieldCtrl.getRawValue() as IFields;

        if (
          fieldVal.id !== this.fieldId &&
          !excludedTypes.includes(fieldVal.type) &&
          !this.isScaleField(fieldVal.type)
        ) {
          allFields.push(fieldVal);
        }
      });
    });

    return allFields;
  }

  /**
   * @returns {boolean} it tells
   * if the delete button should be disabled
   */
  get disableDelete(): boolean {
    const parentArray = this.fieldForm.parent as FormArray;
    return parentArray ? parentArray.length <= 1 : true;
  }

  /**
   * @returns {boolean} it tells if the condition is broken,
   */
  get isConditionBroken(): boolean {
    return !this.dynamicFormService.isConditionStillValid(this.fieldForm);
  }

  /**
   * helper function to track options
   */
  trackByOption(index: number): number {
    return index;
  }

  /**
   * Open test connessione dialog.
   */
  openTestConnessioneDialog(): void {
    const endpoint = this.endpointControl?.value?.id;

    this.smartModalService
      .openModalFromComponent(TestConnessioneDialogComponent, {
        config: { data: endpoint, minWidth: '500px' },
      } as CustomModalOptions)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  /**
   * Checks if the field type is a scale field.
   */
  isScaleField(type: FieldTypeENUM): boolean {
    return this.dynamicFormService.isScaleField(type);
  }

  /**
   * @param {AbstractControl} field The nested field control.
   * @returns {FormGroup} The form group for the nested field.
   */
  getFieldFormGroup(field: AbstractControl): FormGroup {
    return field as FormGroup;
  }

  /**
   * Tracks nested accordion fields.
   */
  trackByField = (_index: number, control: AbstractControl) =>
    control.get('id')?.value || _index;
}
